// Client-side search and sorting rules (SPEC §10.3, §10.5).

const APOSTROPHE_VARIANTS: RegExp = /[’ʼ]/g;

/** The form used to compare search text: NFC, lowercase, every apostrophe variant as "'". */
export function toSearchKey(text: string): string {
  return text.normalize("NFC").toLocaleLowerCase().replace(APOSTROPHE_VARIANTS, "'");
}

/** True when the text contains the query, ignoring case and apostrophe variants (SPEC §10.5). */
export function matchesSearch(text: string, query: string): boolean {
  const trimmedQuery: string = query.trim();
  if (trimmedQuery === "") {
    return true;
  }

  return toSearchKey(text).includes(toSearchKey(trimmedQuery));
}

/** A comparer using the UI language's collation (Ukrainian alphabet order incl. Ґ Є І Ї). */
export function createNameComparer(locale: string): (first: string, second: string) => number {
  const collator: Intl.Collator = new Intl.Collator(locale, { sensitivity: "base" });
  return (first: string, second: string): number => collator.compare(first, second);
}

/** Sorts people by last name, then first name, in the UI language's order. */
export function sortByName<T extends { firstName: string; lastName: string }>(people: T[], locale: string): T[] {
  const compare: (first: string, second: string) => number = createNameComparer(locale);
  return [...people].sort((first: T, second: T): number => {
    const byLastName: number = compare(first.lastName, second.lastName);
    return byLastName !== 0 ? byLastName : compare(first.firstName, second.firstName);
  });
}
